import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import { throttle } from 'lodash';
import hoistNonReactStatics from 'hoist-non-react-statics';
import invariant from 'invariant';

const THROTTLE_TIME = 200;

/** Higher order component (HOC) that pass the component width as prop.
 *
 * class SomeComponent {
 *   static propTypes = {
 *     width: PropTypes.number.isRequired <- isRequired, can be use in the first render!
 *   }
 *
 *   render() {
 *     return <div>my width is {this.props.width} pixels</div>;
 *   }
 * }
 *
 * export default withWidth(SomeComponent);
 */

/* Placeholder is a component that we use to compute
 * the initial size of the children when mounted. We can't
 * just mount the child directly otherwise the width props
 * will be null at first and the child will have to re-render
 * when we'll set the width prop.
 */

class Placeholder extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
  };

  render() {
    const { className } = this.props;
    let { style } = this.props;

    if (!className && !style) {
      style = { width: '100%', height: '100%', position: 'relative' };
    }

    const props = { className, style };
    return <div {...props} />;
  }
}

export default function responsive(Child) {
  class WithWidth extends React.Component {
    static propTypes = {
      reference: PropTypes.func,
      className: PropTypes.string,
      style: PropTypes.object,
      ref: PropTypes.any,
    };

    state = {
      width: null,
    };

    displayName = `withWidth(${Child.displayName})`;

    componentDidMount() {
      invariant(
        !this.props.ref,
        'You cannot pass a ‘ref’ prop to withWidth HOC. Please pass ‘reference’ instead.'
      );
      window.addEventListener('resize', this.computeState);
      this.computeState();
    }

    componentWillUnmount() {
      window.removeEventListener('resize', this.computeState);
    }

    computeState = throttle(
      function() {
        this.node = ReactDOM.findDOMNode(this);
        if (!this.node) {
          return;
        }
        if (this.node.offsetWidth !== this.state.width) {
          this.setState({
            width: this.node.offsetWidth,
          });
        }
      }.bind(this),
      THROTTLE_TIME
    );

    render() {
      if (this.state.width !== null) {
        return <Child {...this.state} {...this.props} ref={this.props.reference} />;
      } else {
        return <Placeholder />;
      }
    }
  }

  return hoistNonReactStatics(WithWidth, Child);
}
